import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, Check, Loader2, Star, Sparkles, Droplets
} from 'lucide-react';
import PublicLegalFooter from '@/components/PublicLegalFooter';
import CheckoutConsent from '@/components/CheckoutConsent';

export default function OnboardingPlan() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [identifiedVehicle, setIdentifiedVehicle] = useState(null);
  const [availablePlans, setAvailablePlans] = useState({});
  const [vehicleTypeDisplay, setVehicleTypeDisplay] = useState('');
  // Consentimento obrigatório de checkout (Termos + Política de Reembolso)
  const [checkoutAccepted, setCheckoutAccepted] = useState(false);
  const [consentVersions, setConsentVersions] = useState({ terms: '1.0', refund: '1.0' });

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    
    checkOnboardingStatus();
  }, [user, navigate]);

  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Se não tem veículo, voltar para etapa anterior
      if (!res.data.has_vehicle) {
        navigate('/client/onboarding/vehicle', { replace: true });
        return;
      }

      // Se já concluiu a contratação (plano ATIVO ou PENDENTE efetivado no gateway),
      // ir para o dashboard — nunca reiniciar o onboarding.
      if (res.data.has_active_plan || res.data.has_pending_plan) {
        navigate('/client/dashboard', { replace: true });
        return;
      }

      // Se ainda não preencheu os dados para assinatura, voltar para essa etapa
      if (!res.data.has_subscription_data) {
        navigate('/client/onboarding/subscription-data', { replace: true });
        return;
      }
      
      // Recuperar dados do veículo da sessão apenas para exibição
      const vehicleData = sessionStorage.getItem('identified_vehicle');
      const typeDisplay = sessionStorage.getItem('vehicle_type_display');
      
      if (vehicleData) {
        const vehicle = JSON.parse(vehicleData);
        setIdentifiedVehicle(vehicle);
        setVehicleTypeDisplay(typeDisplay || '');
        
        // SEMPRE buscar planos da API (NUNCA usar sessionStorage para planos)
        await fetchPlansFromAPI(vehicle.vehicle_type);
      } else {
        // Se não tem dados na sessão, buscar veículos e determinar planos
        await fetchVehicleAndPlans();
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  // Função dedicada para buscar planos da API
  const fetchPlansFromAPI = async (vehicleType) => {
    try {
      const plansRes = await axios.get(`${API}/vehicles/plans-by-type/${vehicleType}`);
      console.log('PLANOS RECEBIDOS DA API:', plansRes.data);
      setAvailablePlans(plansRes.data.plans || {});
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
      setAvailablePlans({});
    }
  };

  const fetchVehicleAndPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.vehicles && res.data.vehicles.length > 0) {
        const vehicle = res.data.vehicles[res.data.vehicles.length - 1];
        setIdentifiedVehicle(vehicle);
        setVehicleTypeDisplay({
          small: 'Carro Pequeno',
          suv: 'SUV',
          pickup: 'Caminhonete'
        }[vehicle.vehicle_type] || vehicle.vehicle_type);
        
        // Buscar planos da API
        await fetchPlansFromAPI(vehicle.vehicle_type);
      }
    } catch (error) {
      console.error('Erro ao buscar veículo:', error);
      toast.error('Erro ao carregar dados do veículo');
    }
  };

  const handleSelectPlan = async (planKey) => {
    if (!identifiedVehicle) {
      toast.error('Veículo não encontrado. Cadastre um veículo primeiro.');
      navigate('/client/onboarding/vehicle');
      return;
    }

    if (!checkoutAccepted) {
      toast.error('Para continuar, aceite os Termos de Uso e a Política de Reembolso.');
      return;
    }

    setSelectedPlan(planKey);
    setLoading(true);

    try {
      const plan = availablePlans[planKey];
      // Redirecionar diretamente para pagamento usando plan_id
      navigate('/client/payment', {
        state: {
          vehicle_id: identifiedVehicle.id,
          plan_id: plan?.plan_key || planKey,
          plan_name: plan?.name,
          plan_price: plan?.price,
          from_onboarding: true,
          accepted_terms_version: consentVersions.terms,
          accepted_refund_policy_version: consentVersions.refund,
          accepted_checkout_at: new Date().toISOString()
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao selecionar plano');
      setLoading(false);
    }
  };

  // Ordenar planos por preço (menor para maior)
  const plansToDisplay = Object.entries(availablePlans)
    .sort(([, a], [, b]) => (a.price || 0) - (b.price || 0));

  const getPlanIcon = (planKey, plan) => {
    const name = plan?.name?.toLowerCase() || '';
    if (planKey.includes('premium') || name.includes('premium')) return <Star className="w-5 h-5" />;
    if (planKey.includes('intermediate') || name.includes('intermediário')) return <Sparkles className="w-5 h-5" />;
    return <Droplets className="w-5 h-5" />;
  };

  const getPlanColor = (planKey, plan) => {
    const name = plan?.name?.toLowerCase() || '';
    if (planKey.includes('premium') || name.includes('premium')) return 'from-amber-500 to-amber-600';
    if (planKey.includes('intermediate') || name.includes('intermediário')) return 'from-primary to-primary/80';
    return 'from-slate-600 to-slate-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Progress indicator */}
        <div className="mb-6 max-w-md mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-primary">Etapa 3 de 3</span>
            <span className="text-xs text-muted-foreground">Escolher Plano</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: '100%' }} />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center bg-primary/10 p-3 rounded-xl mb-4">
            <CreditCard className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Escolha seu Plano</h1>
          <p className="text-muted-foreground">
            Selecione o plano ideal para você e comece a economizar
          </p>
        </div>

        {/* Veículo identificado */}
        {identifiedVehicle && vehicleTypeDisplay && (
          <div className="mb-8 text-center">
            <div className="inline-block bg-white border rounded-lg px-6 py-3 shadow-sm">
              <p className="text-sm text-muted-foreground mb-1">Planos para:</p>
              <p className="text-lg font-semibold" data-testid="vehicle-info">
                {identifiedVehicle.brand} {identifiedVehicle.model} – {vehicleTypeDisplay}
              </p>
              <p className="text-xs text-muted-foreground">
                Placa: {identifiedVehicle.plate}
              </p>
            </div>
          </div>
        )}

        {/* Planos */}
        {plansToDisplay.length > 0 ? (
          <>
            {/* Consentimento obrigatório de checkout (Termos + Reembolso) */}
            <CheckoutConsent
              accepted={checkoutAccepted}
              onAcceptedChange={setCheckoutAccepted}
              onVersionsChange={setConsentVersions}
            />

            <div className="grid md:grid-cols-3 gap-6 mb-8">
            {plansToDisplay.map(([key, plan]) => {
              const isRecommended = key.includes('intermediate') || plan.is_popular === true || plan.name?.toLowerCase().includes('intermediário');
              const isPremium = key.includes('premium') || plan.name?.toLowerCase().includes('premium');
              
              return (
                <Card 
                  key={key} 
                  className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
                    isRecommended ? 'border-2 border-primary shadow-lg scale-105' : ''
                  } ${selectedPlan === key ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                >
                  {isRecommended && (
                    <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-xs font-bold text-center py-1">
                      MAIS POPULAR
                    </div>
                  )}
                  
                  <div className={`p-6 ${isRecommended ? 'pt-8' : ''}`}>
                    {/* Header do plano */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`bg-gradient-to-r ${getPlanColor(key, plan)} text-white p-2 rounded-lg`}>
                        {getPlanIcon(key, plan)}
                      </div>
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                    </div>
                    
                    {/* Preço */}
                    <div className="mb-4">
                      <p className="text-4xl font-bold text-primary">
                        R$ {plan.price}
                        <span className="text-lg font-normal text-muted-foreground">/mês</span>
                      </p>
                    </div>
                    
                    {/* Descrição */}
                    <p className="text-sm text-muted-foreground mb-4">
                      {plan.description}
                    </p>
                    
                    {/* Benefícios */}
                    <ul className="space-y-2 mb-6">
                      {plan.limits?.complete > 0 && (
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600" />
                          {plan.limits.complete} Lavagem{plan.limits.complete > 1 ? 'ns' : ''} completa{plan.limits.complete > 1 ? 's' : ''}
                        </li>
                      )}
                      {plan.limits?.ducha > 0 && (
                        <li className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600" />
                          {plan.limits.ducha} Ducha{plan.limits.ducha > 1 ? 's' : ''} simples
                        </li>
                      )}
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-600" />
                        Agendamento via app
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-600" />
                        Suporte prioritário
                      </li>
                    </ul>
                    
                    {/* Botão */}
                    <Button 
                      onClick={() => handleSelectPlan(key)}
                      disabled={loading || !checkoutAccepted}
                      className={`w-full ${isRecommended ? '' : 'bg-slate-800 hover:bg-slate-900'}`}
                      data-testid={`select-plan-${key}`}
                    >
                      {loading && selectedPlan === key ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processando...
                        </>
                      ) : (
                        'Selecionar Plano'
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
          </>
        ) : (
          <Card className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Carregando planos disponíveis...</p>
          </Card>
        )}

        {/* Rodapé público de compliance jurídico */}
        <div className="pt-6 mt-6 border-t border-slate-200">
          <PublicLegalFooter />
        </div>
      </div>
    </div>
  );
}
